import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { getDecryptedVideos, textsByAxis } from "../utils/utils.ts";
import { Box, Typography, CircularProgress, Alert, Button, Fade } from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import { colors, fonts, shadows, radii, compactCardBreakpoint, shortViewportBreakpoint } from "../theme/tokens.ts";

const OUTRO_VIDEO_ID = "so1E4Q73ypA";

// Tipos mínimos de la YouTube IFrame Player API (no tenemos @types/youtube instalado).
type YTPlayer = {
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;
  playVideo: () => void;
};

declare global {
  interface Window {
    YT?: {
      Player: new (
        el: HTMLIFrameElement,
        options: { events?: { onStateChange?: (event: { data: number }) => void } }
      ) => YTPlayer;
      PlayerState: { ENDED: number };
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

// Carga el script de la IFrame API una sola vez, aunque se monte varias veces el video.
let ytApiPromise: Promise<void> | null = null;
const loadYouTubeApi = (): Promise<void> => {
  if (window.YT?.Player) return Promise.resolve();
  if (!ytApiPromise) {
    ytApiPromise = new Promise((resolve) => {
      const previousCallback = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        previousCallback?.();
        resolve();
      };
      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      // Si falla la carga, se permite reintentar en el próximo montaje. El video
      // se sigue viendo igual, solo sin el overlay de fin.
      script.onerror = () => {
        ytApiPromise = null;
      };
      document.head.appendChild(script);
    });
  }
  return ytApiPromise;
};

// Iframe de YouTube controlado por la IFrame API. Al terminar el video se tapa el
// player con la miniatura y un botón "Ver de nuevo", para que no se vea la grilla
// de videos sugeridos que YouTube muestra al final (rel=0 solo la limita al canal).
// Se renderiza dentro de un contenedor position:relative que define el tamaño.
const OutroVideo = () => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const playerRef = useRef<YTPlayer | null>(null);
  const [ended, setEnded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadYouTubeApi().then(() => {
      const YT = window.YT;
      if (cancelled || !iframeRef.current || !YT) return;
      playerRef.current = new YT.Player(iframeRef.current, {
        events: {
          onStateChange: (event) => {
            if (event.data === YT.PlayerState.ENDED) setEnded(true);
          },
        },
      });
    });
    return () => {
      cancelled = true;
      playerRef.current = null;
    };
  }, []);

  const handleReplay = () => {
    playerRef.current?.seekTo(0, true);
    playerRef.current?.playVideo();
    setEnded(false);
  };

  return (
    <>
      <Box
        component="iframe"
        ref={iframeRef}
        src={`https://www.youtube-nocookie.com/embed/${OUTRO_VIDEO_ID}?rel=0&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`}
        title="Seguir profundizando"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          border: 0,
        }}
      />
      {ended && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0.45)), url(https://i.ytimg.com/vi/${OUTRO_VIDEO_ID}/hqdefault.jpg)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <Button
            onClick={handleReplay}
            disableRipple
            startIcon={<ReplayIcon />}
            sx={{
              background: colors.coral,
              color: "#ffffff",
              borderRadius: `${radii.pill}px`,
              padding: "10px 20px",
              fontFamily: fonts.heading,
              fontWeight: 600,
              fontSize: "14px",
              textTransform: "none",
              "&:hover": { background: colors.coralHover },
            }}
          >
            Ver de nuevo
          </Button>
        </Box>
      )}
    </>
  );
};

const INTRO_PARAGRAPHS = [
  "Llegaste al final del cuestionario, y eso ya dice algo de vos. No todo el mundo se anima a mirarse de frente, a responder con honestidad lo que le incomoda. Vos lo hiciste.",
  "Lo que vas a encontrar acá no es un diagnóstico ni una sentencia: es un mapa. Un punto de partida para entenderte mejor, para ver qué cosas ya tenés a favor y en qué áreas vale la pena poner el foco.",
  "Leé con calma. Hay cosas que tal vez ya sabías, y otras que quizás te sorprendan. Cualquiera de las dos tiene valor.",
];

// Reemplazado por el video de YouTube en la página de outro. Se deja comentado por si se necesita volver a este copy.
// const OUTRO_PARAGRAPHS = [
//   "Si alguno de estos resultados te hizo mirar de otra manera algo que te está pasando, podés profundizar ese recorrido.",
//   "El mapa de tu vida fue creado por nuestra institución precisamente para esto: ayudarte a comprender mejor dónde estás, reconocer qué depende de vos y construir una dirección posible desde la cual volver a avanzar.",
// ];

// Demora antes de mostrar el CTA de la página de outro: la idea es que el
// usuario no lo vea de entrada y primero le dé play al video.
const OUTRO_CTA_DELAY_MS = 30000;

// Renderiza a sus hijos recién pasado `delayMs` desde que se monta. Como el
// bloque de outro se desmonta al navegar, el contador se reinicia cada vez
// que el usuario vuelve a esa página.
const DelayedReveal = ({ delayMs, children }: { delayMs: number; children: React.ReactElement }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), delayMs);
    return () => clearTimeout(timer);
  }, [delayMs]);

  return visible ? <Fade in timeout={600}>{children}</Fade> : null;
};

type ResultPage =
  | { type: "intro" }
  | { type: "axis"; key: string }
  | { type: "outro" };

// Splits on **bold** markers (added in textsByAxis) and renders the wrapped
// segments as <strong>, leaving the rest of the text untouched.
const renderWithBold = (text: string) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((segment, i) => {
    if (segment.startsWith("**") && segment.endsWith("**")) {
      return <strong key={i}>{segment.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{segment}</React.Fragment>;
  });

export const Results = () => {
  const { encryptedVideos } = useParams();
  const [axisKeys, setAxisKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!encryptedVideos) {
      setLoading(false);
      return;
    }
    getDecryptedVideos(encryptedVideos)
      .then((decrypted) => {
        setAxisKeys(decrypted.split("-").filter(Boolean));
        setLoading(false);
      })
      .catch(() => {
        setError("No se pudieron cargar tus resultados. Intentá de nuevo.");
        setLoading(false);
      });
  }, [encryptedVideos]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 3,
          bgcolor: "transparent",
          padding: "60px 16px",
        }}
      >
        <CircularProgress size={40} sx={{ color: colors.coral }} />
        <Typography
          sx={{
            fontFamily: fonts.body,
            color: colors.muted,
            letterSpacing: 2,
            fontSize: "0.75rem",
            textTransform: "uppercase",
          }}
        >
          Cargando tus resultados
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ bgcolor: "transparent", padding: "40px 16px" }}>
        <Box sx={{ maxWidth: 560, mx: "auto" }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Box>
    );
  }

  const visibleAxisKeys = axisKeys.filter((key) => textsByAxis[key]);
  const totalAxis = visibleAxisKeys.length;
  const pages: ResultPage[] = [
    { type: "intro" },
    ...visibleAxisKeys.map((key) => ({ type: "axis" as const, key })),
    { type: "outro" },
  ];

  const safeIndex = Math.min(currentIndex, pages.length - 1);
  const currentPage = pages[safeIndex];
  const isFirst = safeIndex === 0;
  const isLast = safeIndex === pages.length - 1;
  // Pages 1..totalAxis are the axis pages, so the page index doubles as the 1-based axis number.
  const axisNumber = currentPage.type === "axis" ? safeIndex : 0;
  const progressPct = totalAxis > 0 ? Math.round(((axisNumber - 1) / totalAxis) * 100) : 0;

  const handleNext = () => {
    if (!isLast) setCurrentIndex((prev) => prev + 1);
  };

  const handlePrevious = () => {
    if (!isFirst) setCurrentIndex((prev) => prev - 1);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        bgcolor: "transparent",
        padding: "24px 16px",
        // Alto fijo, sin scroll propio del documento: el embed real usa un
        // iframe con height:100vh sin auto-resize. Si esta pantalla tuviera
        // su propio scroll de documento, un swipe puede "escaparse" hacia la
        // página de WordPress que lo embebe antes de llegar al final del
        // contenido, dejando tapados tanto el resto del texto como los
        // botones. Acá adentro, lo único que scrollea es el texto de la
        // tarjeta (ver más abajo): el título, la barra de progreso y los
        // botones quedan siempre en el mismo lugar, en cualquier pantalla.
        height: "100vh",
        "@supports (height: 100dvh)": { height: "100dvh" },
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ maxWidth: 640, width: "100%", height: "100%", display: "flex", flexDirection: "column", gap: "12px", overflow: "hidden" }}>
        {/*
          En mobile (xs) se usan las flechas chicas de acá arriba en vez de
          los botones grandes de abajo: quedan garantizadas en la primera
          pantalla, sin depender de que el usuario llegue a hacer scroll
          hasta el final (donde, en el embed real de WordPress, pueden
          terminar tapadas por el contenido que sigue después del iframe).
          En desktop (md+) se ven los botones grandes de abajo, como siempre.
        */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: currentPage.type === "axis" ? "space-between" : "flex-end", flexShrink: 0 }}>
          {currentPage.type === "axis" && (
            <Typography
              sx={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: "15px", color: colors.teal }}
            >
              Resultado {axisNumber} de {totalAxis}
            </Typography>
          )}
          <Box sx={{ display: { xs: "flex", md: "none" }, gap: "8px" }}>
            <Button
              onClick={handlePrevious}
              disableRipple
              disabled={isFirst}
              aria-label="Anterior"
              sx={{
                minWidth: 0,
                width: "32px",
                height: "32px",
                padding: 0,
                borderRadius: "50%",
                background: "#ffffff",
                color: colors.teal,
                border: `1.5px solid ${colors.borderInput}`,
                fontSize: "14px",
                opacity: isFirst ? 0.35 : 1,
                "&.Mui-disabled": { color: colors.teal, opacity: 0.35 },
              }}
            >
              ←
            </Button>
            {!isLast && (
              <Button
                onClick={handleNext}
                disableRipple
                aria-label="Siguiente"
                sx={{
                  minWidth: 0,
                  width: "32px",
                  height: "32px",
                  padding: 0,
                  borderRadius: "50%",
                  background: colors.coral,
                  color: "#ffffff",
                  fontSize: "14px",
                  "&:hover": { background: colors.coralHover },
                }}
              >
                →
              </Button>
            )}
          </Box>
        </Box>

        {currentPage.type === "axis" && (
          <Box
            sx={{
              width: "100%",
              height: "5px",
              borderRadius: `${radii.pill}px`,
              background: colors.borderLight,
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${progressPct}%`,
                background: colors.coral,
                borderRadius: `${radii.pill}px`,
                transition: "width 0.3s ease",
              }}
            />
          </Box>
        )}

        {/*
          Única región con scroll de toda la pantalla: si el texto del eje es
          más largo que el espacio disponible, scrollea acá adentro, acotado
          y sin arrastrar al resto del layout. overscrollBehavior:"contain"
          evita explícitamente que, al llegar al final de este scroll, el
          gesto siga y termine moviendo la página de WordPress de afuera.

          Es un wrapper transparente (sin fondo/padding propio) para que,
          cuando el texto es corto, la tarjeta de abajo no quede estirada
          ocupando todo el espacio libre — se ve con su tamaño natural, y el
          espacio libre queda en blanco antes de los botones, que igual
          quedan siempre pegados abajo de todo.
        */}
        <Box
          sx={{
            // flexGrow solo en mobile (xs): ahí los botones grandes de abajo
            // están ocultos (se usan las flechas de arriba), así que estirar
            // este wrapper para ocupar todo el espacio libre no afecta nada
            // visible. En desktop (md+) NO debe crecer: si crece, empuja los
            // botones grandes al borde inferior de la pantalla en vez de
            // dejarlos justo debajo de la tarjeta, que es como se ve mal.
            // minHeight:0 es lo que sí hace falta siempre, para que si el
            // texto no entra, se achique y scrollee acá adentro en vez de
            // desbordar la página.
            flexGrow: { xs: 1, md: 0 },
            minHeight: 0,
            overflowY: "auto",
            overscrollBehavior: "contain",
            [shortViewportBreakpoint]: {
              maxHeight: "40vh",
            },
            [compactCardBreakpoint]: {
              maxHeight: "60vh",
            },
          }}
        >
          <Box
            sx={{
              background: currentPage.type === "intro" ? colors.coral : "#ffffff",
              borderRadius: radii.card,
              padding: { xs: "22px", md: "28px" },
              boxShadow: shadows.card,
              display: "flex",
              flexDirection: "column",
              gap: "14px",
              textAlign: currentPage.type === "axis" ? "left" : "center",
            }}
          >
            {currentPage.type === "intro" && (
              <>
                <Typography
                  sx={{ fontFamily: fonts.heading, fontWeight: 800, fontSize: "23px", color: "#ffffff" }}
                >
                  Tus resultados
                </Typography>
                {INTRO_PARAGRAPHS.map((text, i) => (
                  <Typography
                    key={i}
                    sx={{ fontFamily: fonts.body, fontSize: "15px", lineHeight: 1.65, color: colors.resultsIntroText }}
                  >
                    {text}
                  </Typography>
                ))}
              </>
            )}

            {currentPage.type === "axis" &&
              (() => {
                const entry = textsByAxis[currentPage.key];
                const paragraphs = entry.text.split("\n\n");
                return (
                  <>
                    <Typography
                      sx={{
                        fontFamily: fonts.heading,
                        fontWeight: 700,
                        fontSize: "18px",
                        color: colors.teal,
                        lineHeight: 1.4,
                      }}
                    >
                      {entry.title}
                    </Typography>
                    {paragraphs.map((paragraph, pIndex) => (
                      <Typography
                        key={pIndex}
                        sx={{ fontFamily: fonts.body, fontSize: "15px", lineHeight: 1.65, color: colors.resultsDetailText }}
                      >
                        {renderWithBold(paragraph)}
                      </Typography>
                    ))}
                  </>
                );
              })()}

            {currentPage.type === "outro" && (
              <>
                {/* Texto reemplazado por el video de YouTube de abajo. Se deja comentado por si se necesita volver a este copy.
                {OUTRO_PARAGRAPHS.map((text, i) => (
                  <Typography
                    key={i}
                    sx={{ fontFamily: fonts.body, fontSize: "15px", lineHeight: 1.65, color: colors.muted }}
                  >
                    {text}
                  </Typography>
                ))}
                */}
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    paddingTop: "56.25%",
                    borderRadius: radii.card,
                    overflow: "hidden",
                    // En viewports bajos pero anchos (ej. ventana de 1440x540) el truco de
                    // paddingTop basado en el ancho generaba un video gigante que tapaba el
                    // botón de abajo. Ahí fijamos la altura directamente y dejamos que el
                    // ancho se derive del aspect ratio, en vez de al revés.
                    [shortViewportBreakpoint]: {
                      alignSelf: "center",
                      width: "auto",
                      paddingTop: 0,
                      height: "22vh",
                      aspectRatio: "16 / 9",
                    },
                  }}
                >
                  <OutroVideo />
                </Box>
                <Box sx={{ width: "32px", height: "2px", background: colors.coral, alignSelf: "center", my: "4px" }} />
                <DelayedReveal delayMs={OUTRO_CTA_DELAY_MS}>
                  <Button
                    component="a"
                    href="https://reinventatuvida.org/adquisicion/"
                    target="_top"
                    disableRipple
                    sx={{
                      alignSelf: "center",
                      background: colors.coral,
                      color: "#ffffff",
                      borderRadius: `${radii.pill}px`,
                      padding: "12px 24px",
                      fontFamily: fonts.heading,
                      fontWeight: 600,
                      fontSize: "15px",
                      textTransform: "none",
                      "&:hover": { background: colors.coralHover },
                    }}
                  >
                    Seguir profundizando
                  </Button>
                </DelayedReveal>
              </>
            )}
          </Box>
        </Box>

        <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", justifyContent: isLast ? "flex-start" : "space-between", flexShrink: 0 }}>
          <Button
            onClick={handlePrevious}
            disableRipple
            disabled={isFirst}
            sx={{
              background: "#ffffff",
              color: colors.teal,
              border: `1.5px solid ${colors.borderInput}`,
              borderRadius: `${radii.pill}px`,
              padding: "11px 20px",
              fontFamily: fonts.heading,
              fontWeight: 600,
              fontSize: "14px",
              textTransform: "none",
              opacity: isFirst ? 0.35 : 1,
              "&.Mui-disabled": { color: colors.teal, opacity: 0.35 },
            }}
          >
            ← Anterior
          </Button>

          {!isLast && (
            <Button
              onClick={handleNext}
              disableRipple
              sx={{
                background: colors.coral,
                color: "#ffffff",
                borderRadius: `${radii.pill}px`,
                padding: "11px 22px",
                fontFamily: fonts.heading,
                fontWeight: 600,
                fontSize: "14px",
                textTransform: "none",
                "&:hover": { background: colors.coralHover },
              }}
            >
              Siguiente →
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Results;
